import type {
  PatientPageAboutThem,
  PatientPageAppointment,
  PatientPageHeader,
} from "../../../shared/api-types";
import { visitDateLabel } from "./patients-client";

// The patient page (NO-849): what each part of GET /api/patients/:patientId
// reads as. Every absent value is left out, never shown as a placeholder.
// Dates read in the device time zone, as on the Patients list. Pure and
// relative-import only, so `tsx --test` runs it.

type RichTextNode = PatientPageAboutThem["document"];

export function patientPageQueryKey(patientId: string): string[] {
  return ["patient-page", patientId];
}

/** The facts under the name, in the design's order, absent ones left out. */
export function patientHeaderFacts(
  header: PatientPageHeader,
  timezone: string,
  now: Date
): string[] {
  const facts: (string | null)[] = [
    header.age === undefined ? null : String(header.age),
    header.insuranceDescription ?? null,
    header.referredBy ? `Referred by ${header.referredBy.name}` : null,
    header.firstVisitAt
      ? `First visit ${visitDateLabel(header.firstVisitAt, timezone, now)}`
      : null,
    header.nextVisitAt
      ? `Next visit ${visitDateLabel(header.nextVisitAt, timezone, now)}`
      : null,
  ];

  return facts.filter((fact): fact is string => fact !== null);
}

/** "Wed Sep 30", with the year added when it is not the current one. */
export function appointmentDateLabel(iso: string, timezone: string, now: Date): string {
  const yearOf = (date: Date) =>
    new Intl.DateTimeFormat("en-US", { timeZone: timezone, year: "numeric" }).format(
      date
    );
  const date = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "short",
    month: "short",
    day: "numeric",
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((entry) => entry.type === type)?.value ?? "";
  const label = `${part("weekday")} ${part("month")} ${part("day")}`;

  return yearOf(date) === yearOf(now) ? label : `${label}, ${yearOf(date)}`;
}

/** "Follow-up · 45 min", or just the type when the length is unknown. */
export function appointmentKindLabel(
  appointment: Pick<PatientPageAppointment, "appointmentType" | "lengthMinutes">
): string {
  return appointment.lengthMinutes === undefined
    ? appointment.appointmentType
    : `${appointment.appointmentType} · ${appointment.lengthMinutes} min`;
}

/** "Updated after the Sep 30 visit", or null when no visit precedes the note. */
export function aboutThemUpdatedLabel(
  aboutThem: Pick<PatientPageAboutThem, "updatedAfter">,
  timezone: string,
  now: Date
): string | null {
  if (!aboutThem.updatedAfter) return null;

  return `Updated after the ${visitDateLabel(aboutThem.updatedAfter.startsAt, timezone, now)} visit`;
}

function hasText(node: RichTextNode): boolean {
  if (node.text?.trim()) return true;

  return (node.content ?? []).some(hasText);
}

/** A note never saved, or saved with no text, reads as empty. */
export function isQuickNoteEmpty(
  aboutThem: Pick<PatientPageAboutThem, "document" | "revision">
): boolean {
  return aboutThem.revision === null || !hasText(aboutThem.document);
}

/**
 * About them shows only when the server sent it (own lane, read permission,
 * patient-quick-note on) and the viewer's flags also have patient-quick-note on.
 */
export function visibleAboutThem(
  aboutThem: PatientPageAboutThem | undefined,
  quickNoteEnabled: boolean
): PatientPageAboutThem | null {
  return quickNoteEnabled ? (aboutThem ?? null) : null;
}
